import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Helmet } from "react-helmet-async";
import fileDownload from "js-file-download";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from '@mui/icons-material/Download';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import DateAdapter from "@mui/lab/AdapterDateFns";
import DatePicker from "@mui/lab/DatePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import {
  DataGrid,
  GridColDef,
} from "@mui/x-data-grid";
import { enGB } from "date-fns/locale";

import { BACKEND_URL, siteConstants } from "../../../constants";
import { alertActions, csActions } from "../../../redux/actions";
import { csServices } from "../../../services";

const TopTenTalentThemesPage = (props) => {
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - siteConstants.CS_REPORT_LIST_DAYS);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(new Date());
  const [reports, setReports] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const columns: GridColDef[] = [
    { field: "date", headerName: "Date", width: 160, },
    { field: "clientName", headerName: "Client Name", width: 200, },
    { field: "theme_1", headerName: "Theme 1", width: 160, },
    { field: "theme_2", headerName: "Theme 2", width: 160, },
    { field: "theme_3", headerName: "Theme 3", width: 160, },
    { field: "theme_4", headerName: "Theme 4", width: 160, },
    { field: "theme_5", headerName: "Theme 5", width: 160, },
    { field: "theme_6", headerName: "Theme 6", width: 160, },
    { field: "theme_7", headerName: "Theme 7", width: 160, },
    { field: "theme_8", headerName: "Theme 8", width: 160, },
    { field: "theme_9", headerName: "Theme 9", width: 160, },
    { field: "theme_10", headerName: "Theme 10", width: 160, },
  ];

  const handleRefreshReports = () => {
    props.listReports(startDate, endDate);
  }

  const handleGridView = () => {
    props.alertReset();
    const shortenIds = selectedIds.map((selectedId) => selectedId.slice(-8));
    const url = `${BACKEND_URL}/top-ten-talents/?ids=${shortenIds.join()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  const handleDownload = () => {
    props.alertReset();
    csServices.downloadTalentThemes(startDate, endDate)
      .then(response => {
        fileDownload(response.data, "top_10_talent_themes.xlsx")
      })
      .catch(error => {
        if (error.response)
          props.alertError(error.response.data.detail || siteConstants.ERROR_MSG_SERVER_COMMUNICATION);
        else
          props.alertError(siteConstants.ERROR_MSG_SERVER_COMMUNICATION);
      });
  }

  const onSelectionModelChange = (selectedIds) => {
    setSelectedIds(selectedIds);
  }

  useEffect(() => {
    const _reports = [];
    if (props.reports) {
      const seen = new Set();
      props.reports.map((report) => {
        const clientName = report.client_name;
        const talentThemes = report.data?.talent_themes;
        const key = `${clientName}${talentThemes?.join()}`;
        if (!seen.has(key)) {
          seen.add(key);
          const talentThemesMap = {};
          if (talentThemes) {
            talentThemes.map((theme, idx) => {
              talentThemesMap[`theme_${idx + 1}`] = theme;
            });
          }
          _reports.push({
            id: report.id,
            date: (new Date(report.created)).toISOString().split("T")[0],
            clientName: clientName,
            ...talentThemesMap,
          })
        }
      });
    }
    setReports(_reports);
  }, [props.reports])

  useEffect(() => {
    props.alertReset();
    props.listReports(startDate, endDate);
  }, [startDate])

  useEffect(() => {
    props.alertReset();
    props.listReports(startDate, endDate);
  }, [endDate])

  useEffect(() => {
    props.alertReset();
  }, [])

  return (
    <>
      <Helmet>
        <title>Top 10 Talent Themes</title>
      </Helmet>

      <LocalizationProvider dateAdapter={DateAdapter} locale={enGB}>
        <Container
          maxWidth="xl"
          sx={{
            marginTop: "30px",
            textAlign: "left",
          }}
        >
          <Typography variant="h4">Top 10 Talent Themes</Typography>
          {
            props.alert.message &&
            <Alert variant="outlined" severity={ props.alert.type }>
              { props.alert.message }
            </Alert>
          }
          <Box
            sx={{
              display: "flex",
              gap: "20px",
              marginTop: "20px",
              marginBottom: "20px",
            }}
          >
            <DatePicker
              label="Start date"
              value={startDate}
              maxDate={new Date()}
              onChange={(newDate) => {
                setStartDate(newDate)
              }}
              renderInput={(params) => <TextField {...params} />}
            />
            <DatePicker
              label="End date"
              value={endDate}
              maxDate={new Date()}
              onChange={(newDate) => {
                setEndDate(newDate)
              }}
              renderInput={(params) => <TextField {...params} />}
            />
            <IconButton
              size="large"
              onClick={handleRefreshReports}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              size="large"
              onClick={handleGridView}
              sx={{
                marginLeft: "auto",
                alignSelf: "flex-end",
              }}
              disabled={selectedIds.length === 0}
            >
              <ViewComfyIcon />
            </IconButton>
            <IconButton
              size="large"
              onClick={handleDownload}
              sx={{
                alignSelf: "flex-end",
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Box>
          <DataGrid
            rows={reports}
            columns={columns}
            autoHeight={true}
            density="compact"
            hideFooterSelectedRowCount={true}
            loading={props.isListingReports}
            checkboxSelection={true}
            onSelectionModelChange={onSelectionModelChange}
          />
        </Container>
      </LocalizationProvider>
    </>
  );
}

const mapState = (state) => {
  const {
    isListingReports,
    reports,
  } = state.cs;
  return {
    isListingReports,
    reports,
    alert: state.alert,
  }
};

const actionCreators = {
  alertReset: alertActions.reset,
  alertError: alertActions.error,
  listReports: csActions.listReports,
};

export default connect(mapState, actionCreators)(TopTenTalentThemesPage);
